import type {AuthenticateModalProps} from '@Modals/authenticate-modal';
import type {DeepPartial} from '@/global';

type TooltipLabel = string;
export type PromptSuggestionsValue = DefaultPromptSuggestions | RestaurantPromptSuggestions | EmailSingleRewrite | DesignPromptSuggestions;
export type PromptOptionValue = EmojiUsage | AiModel | Styles | AspectRatio | RedesignMood | RewriteMood;
export type SelectedPromptOptionValues = Record<PromptOption, PromptOptionValue>;
export type EditableComponentsMap = Partial<Record<EditableComponent, boolean>>;
export type PromptOptionsForEditableComponents = Partial<Record<EditableComponent, PromptOption[]>>;
export type UnselectableEditableComponentsMap = Partial<Record<EditableComponent, TooltipLabel>>;
export type PromptSuggestionsEnum = typeof DefaultPromptSuggestions | typeof RestaurantPromptSuggestions | typeof EmailSingleRewrite | typeof DesignPromptSuggestions;
export type PromptAttachment = ImageAttachment | DesignAttachment;

export interface OneOfPromptOption {
  option: PromptOption;
  value: PromptOptionValue;
}

export interface RedesignIssue {
  type: RedesignIssueType;
  icon: string;
  message: string;
  showRetryButton?: boolean;
}

export interface HeaderContent {
  title: string;
  icon?: string;
  showPulsatingDot: boolean;
  iconBeforeText?: boolean;
}

export interface UpsellDialogContent {
  dialogName: string;
  showDialog: boolean;
}

export interface CreditsPillContent {
  remainingCredits: number;
  actionCost: number;
  isMaxEstimate?: boolean;
}

export interface CreditUsageContent {
  creditsCharged: number;
  remainingCredits: number;
}

export interface AuthenticateModalContent {
  showModal: boolean;
  modalProps: AuthenticateModalProps;
}

export interface AiRedesignPromptBoxProps {
  regenerateContent: (prompt: string, options: SelectedPromptOptionValues, editableComponentsMap?: EditableComponentsMap, promptAttachments?: PromptAttachment[]) => Promise<void> | void;
  placeholder: string;
  editableComponentsMap: EditableComponentsMap;
  promptOptionsForEditableComponents: PromptOptionsForEditableComponents;
  headerContent?: HeaderContent;
  promptSuggestions?: PromptSuggestions;
  defaultPrompt?: string;
  defaultSelectedPromptOptionValues?: DeepPartial<SelectedPromptOptionValues>;
  promptBoxClasses?: string;
  isPromptBoxLoading?: boolean;
  isPromptBoxSelectedOnRender?: boolean;
  redesignIssueOnRender?: RedesignIssue;
  onPromptChange?: (prompt: string) => void;
  onSelectedPromptOptionValuesChange?: (promptOptionValues: SelectedPromptOptionValues) => void;
  onEditableComponentsMapChange?: (editableComponentsMap: EditableComponentsMap) => void;
  onRedesignIssue?: (issue: RedesignIssue | undefined) => void;
  recentPrompt?: string | (() => string | undefined);
  upsellDialog?: UpsellDialogContent;
  creditsPillContent?: CreditsPillContent;
  creditUsage?: CreditUsageContent;
  unselectOnOutsideClick?: boolean;
  showInitialAnimation?: boolean;
  authenticateModalContent?: AuthenticateModalContent;
  unselectableEditableComponents?: UnselectableEditableComponentsMap;
  numSuggestionsToShow?: number;
  startsFromIcon?: boolean;
  moodType?: MoodType;
  containerClasses?: string;
  onStartingIconClicked?: VoidFunction;
  onContextualMenuOpened?: VoidFunction;
  onPromptOptionChanged?: (promptOption: PromptOption, promptOptionValue: PromptOptionValue) => void;
  isEditableDuringGeneration?: boolean;
  showRefreshSuggestions?: boolean;
  onRefreshSuggestions?: VoidFunction;
  onRecentPromptSelected?: VoidFunction;
  onPromptSuggestionSelected?: (value: PromptSuggestionsValue) => void;
  allowPromptAttachments?: boolean;
  seasonalPromptPill?: SeasonalPromptPill;
}

export interface ImageAttachment {
  hashedFilename: string;
  extension: string;
  source: string;
  type: PromptAttachmentType.IMAGE;
}

export interface DesignAttachment {
  designId: string;
  type: PromptAttachmentType.DESIGN;
  previewURL: string;
  pageHashedId?: string;
}

export interface SeasonalPromptPill {
  label: string;
  prompts: string[];
  labelClasses?: string;
}

export enum PromptOption {
  MOOD = 'mood',
  EMOJI_USAGE = 'emoji_usage',
  AI_MODEL = 'ai_model',
  STYLES = 'styles',
  ASPECT_RATIO = 'aspect_ratio',
}

export enum RedesignMood {
  Formal = 'formal',
  Bold = 'bold',
  Minimal = 'minimal',
  Elegant = 'elegant',
  Vibrant = 'vibrant',
  Modern = 'modern',
  Classic = 'classic',
  Creative = 'creative',
  Playful = 'playful',
  Sleek = 'sleek',
}

export enum RewriteMood {
  Casual = 'casual',
  Professional = 'professional',
  Bold = 'bold',
  Elegant = 'elegant',
  Playful = 'playful',
  Engaging = 'engaging',
  Creative = 'creative',
}

export enum Styles {
  Photographic = 'photographic',
  DigitalArt = 'digital-art',
  Model3d = '3d-model',
  Cinematic = 'cinematic',
  ComicBook = 'comic-book',
  NeonPunk = 'neon-punk',
  LineArt = 'line-art',
  FantasyArt = 'fantasy-art',
  Anime = 'anime',
  PixelArt = 'pixel-art',
  AnalogFilm = 'analog-film',
}

export enum AspectRatio {
  Square = 'SQUARE',
  LandScape = 'LANDSCAPE',
  Portrait = 'PORTRAIT',
}

export enum EmojiUsage {
  Enabled = 'enabled',
  Disabled = 'disabled',
}

export enum AiModel {
  Auto = 'auto',
  NanoBanana = 'nanoBanana',
  Stability = 'stability',
  StabilityUltra = 'stabilityUltra',
  Flux = 'flux',
}

export enum PromptSuggestions {
  DEFAULT = 'default',
  RESTAURANT = 'restaurant',
  EMAIL_SINGLE_REWRITE = 'email_single_rewrite',
  DESIGN = 'design',
}

export enum DefaultPromptSuggestions {
  CHURCH_NEWLETTER = 'church_newletter',
  SALE = 'sale',
  RESTAURANT_DAILY_SPECIALS = 'restaurant_daily_specials',
  SCHOOL_NEWSLETTER = 'school_newsletter',
}

export enum RestaurantPromptSuggestions {
  CAFE_DAILY_SPECIALS = 'cafe_daily_specials',
  BAR_MENU = 'bar_menu',
  HAPPY_HOUR = 'happy_hour',
  COCKTAIL_HOUR = 'cocktail_hour',
}

export enum EmailSingleRewrite {
  CHURCH_EVEMTS = 'email_single_rewrite_church_events',
  SALE = 'email_single_rewrite_sale',
  SUNDAY_SERVICE = 'email_single_rewrite_sunday_service',
  DAILY_SPECIALS = 'email_single_rewrite_daily_specials',
}

export enum DesignPromptSuggestions {
  SAVE_THE_DATE = 'save_the_date',
  BIRTHDAY_INVITE = 'birthday_invite',
  SONG_RELEASE = 'song_release',
  HAPPY_HOUR_PROMOTE = 'happy_hour_promote',
  SUNDAY_SERVICE = 'sunday_service',
  LUNCH_DEALS = 'lunch_deals',
  DJ_NIGHT = 'dj_night',
  DANCE_PARTY = 'dance_party',
  SUMMER_CAMP = 'summer_camp',
  BACK_TO_SCHOOL = 'back_to_school',
  CHARITY_FUNDRAISER = 'charity_fundraiser',
  RETAIL_SALE = 'retail_sale',
  BIBLE_STUDY = 'bible_study',
  SUNDAY_BRUNCH = 'sunday_brunch',
  FOOD_DELIVERY = 'food_delivery',
}

export enum EditableComponent {
  TEXT = 'text',
  IMAGE = 'image',
  STYLES = 'styles',
}

export enum RedesignIssueType {
  ERROR = 'error',
  WARNING = 'warning',
}

export enum CreditsPillTheme {
  DEFAULT = 'default',
  SECONDARY = 'secondary',
  WARNING = 'warning',
}

export enum PromptOptionSelectorLayoutType {
  PILLS = 'pills',
  CARDS = 'cards',
}

export enum MoodType {
  REDESIGN = 'redesign',
  REWRITE = 'rewrite',
}

export enum PromptAttachmentType {
  IMAGE = 'image',
  DESIGN = 'design',
}
